/**
 * company-doc-groups.ts — Wave 71 (บริการที่ 13: เอกสารบริษัท/นิติบุคคล)
 *
 * แหล่งอ้างอิงที่ตรวจสอบได้เท่านั้น:
 *   - กรมพัฒนาธุรกิจการค้า กระทรวงพาณิชย์ (dbd.go.th) — หนังสือรับรองนิติบุคคล บอจ.2/บอจ.3/บอจ.5
 *     บัญชีรายชื่อผู้ถือหุ้น หนังสือบริคณห์สนธิ และงบการเงินที่นำส่ง
 *   - ประมวลกฎหมายแพ่งและพาณิชย์ บรรพ 3 ลักษณะ 22 (หุ้นส่วนบริษัท) — อำนาจกรรมการ การประชุมผู้ถือหุ้น
 *   - พระราชบัญญัติบริษัทมหาชนจำกัด พ.ศ. 2535 — บริษัทมหาชน
 *   - กรมสรรพากร (rd.go.th) — ภ.พ.20 ทะเบียนภาษีมูลค่าเพิ่ม และหนังสือรับรองถิ่นที่อยู่เพื่อการรัษฎากร (R.O.22)
 *   - กรมการกงสุล กระทรวงการต่างประเทศ (consular.mfa.go.th) — งานนิติกรณ์รับรองเอกสารและคำแปล
 *   - สภาทนายความในพระบรมราชูปถัมภ์ — ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney)
 *   - สำนักงานคณะกรรมการส่งเสริมการลงทุน (boi.go.th) — เอกสารประกอบคำขอรับการส่งเสริม
 *   - อนุสัญญากรุงเฮก ค.ศ. 1961 (Apostille) — สถานะของไทยจาก HCCH (src/data/apostille-status.ts)
 *
 * กติกาเนื้อหา: ไม่ระบุราคา ไม่รับประกันผล ไม่กำหนดระยะเวลาราชการเอง
 * เงื่อนไขที่ผันแปรตามหน่วยงาน/ประเทศปลายทาง ให้เขียนว่า "ผันแปร" พร้อมวิธีตรวจสอบจากต้นทาง
 */
import { APOSTILLE_STATUS } from "@/data/apostille-status";

export interface CompanyDocGroup {
  slug: string;
  titleTh: string;
  titleEn: string;
  summaryTh: string;
  summaryEn: string;
  bodyTh: string[];
  bodyEn: string[];
  documentsTh: string[];
  documentsEn: string[];
  chainTh: string[];
  chainEn: string[];
  checklistTh: string[];
  checklistEn: string[];
  faqsTh: { q: string; a: string }[];
  faqsEn: { q: string; a: string }[];
}

export const COMPANY_APOSTILLE_TH =
  `สถานะกฎหมาย: ไทยยื่นภาคยานุวัติสารเข้าเป็นภาคีอนุสัญญากรุงเฮก ค.ศ. 1961 (Apostille) เมื่อ ` +
  `${APOSTILLE_STATUS.accessionDepositTh} และอนุสัญญาจะมีผลบังคับใช้กับประเทศไทยในวันที่ ` +
  `${APOSTILLE_STATUS.entryIntoForceTh} (ที่มา: HCCH) ก่อนถึงวันดังกล่าว หนังสือรับรองบริษัท ` +
  `บัญชีรายชื่อผู้ถือหุ้น และหนังสือมอบอำนาจของนิติบุคคลไทยที่จะใช้ในต่างประเทศ ยังต้องผ่าน ` +
  `การรับรองนิติกรณ์ที่กรมการกงสุล แล้วจึงนำไปรับรองต่อที่สถานทูตของประเทศปลายทางตามลำดับเดิม`;

export const COMPANY_APOSTILLE_EN =
  `Legal status: Thailand deposited its instrument of accession to the 1961 Hague Apostille Convention on ` +
  `${APOSTILLE_STATUS.accessionDepositEn}; it enters into force for Thailand on ${APOSTILLE_STATUS.entryIntoForceEn} ` +
  `(source: HCCH). Until that date, Thai company affidavits, shareholder lists and corporate powers of attorney used ` +
  `abroad still pass through the Department of Consular Affairs and then the destination country's embassy in the ` +
  `traditional order.`;

export const COMPANY_AUTHORITY_TH =
  `หน่วยงานที่รับผิดชอบแยกกันชัดเจน: ทะเบียนนิติบุคคล หนังสือรับรอง บอจ.2 บอจ.3 บอจ.5 และงบการเงิน ` +
  `อยู่ในความดูแลของกรมพัฒนาธุรกิจการค้า กระทรวงพาณิชย์ ส่วนทะเบียนภาษีมูลค่าเพิ่ม (ภ.พ.20) และหนังสือรับรอง ` +
  `ถิ่นที่อยู่เพื่อการรัษฎากรเป็นของกรมสรรพากร การรับรองลายมือชื่อกรรมการในเอกสารที่บริษัทจัดทำขึ้นเองเป็นงานของ ` +
  `ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารตามข้อบังคับสภาทนายความ และการรับรองเพื่อใช้ต่างประเทศเป็นงานนิติกรณ์ ` +
  `ของกรมการกงสุล กระทรวงการต่างประเทศ`;

export const COMPANY_AUTHORITY_EN =
  `Responsibility is clearly split. Company registration records — the certificate of incorporation (affidavit), ` +
  `Bor.Or.Jor.2/3/5 forms, shareholder lists and filed financial statements — sit with the Department of Business ` +
  `Development (DBD), Ministry of Commerce. VAT registration (Por.Por.20) and tax-residence certificates sit with the ` +
  `Revenue Department. Certifying a director's signature on documents the company drafts itself is the work of a ` +
  `Notarial Services Attorney licensed by the Lawyers Council of Thailand, and legalisation for overseas use is handled ` +
  `by the Department of Consular Affairs, Ministry of Foreign Affairs.`;

export const COMPANY_DOC_GROUPS: CompanyDocGroup[] = [
  {
    slug: "company-affidavit-for-overseas-use",
    titleTh: "หนังสือรับรองบริษัท (DBD) เพื่อใช้ในต่างประเทศ",
    titleEn: "Thai company affidavit (DBD) for overseas use",
    summaryTh:
      "หนังสือรับรองนิติบุคคลจากกรมพัฒนาธุรกิจการค้า พร้อมคำแปลภาษาอังกฤษ นำไปรับรองนิติกรณ์ที่กรมการกงสุล และรับรองต่อที่สถานทูตปลายทางเมื่อคู่ค้า ธนาคาร หรือหน่วยงานทะเบียนของต่างประเทศเรียกเอกสารรับรองความมีอยู่ของบริษัท",
    summaryEn:
      "The DBD certificate of incorporation with an English translation, legalised at the Department of Consular Affairs and, where a foreign bank, counterparty or registry requires it, further certified by the destination embassy as proof that the company exists and who may sign for it.",
    bodyTh: [
      "หนังสือรับรองนิติบุคคลเป็นเอกสารที่นายทะเบียนกรมพัฒนาธุรกิจการค้าออกให้ ระบุชื่อบริษัท เลขทะเบียน 13 หลัก วันจดทะเบียน ทุนจดทะเบียน ที่ตั้งสำนักงาน รายชื่อกรรมการ และข้อความสำคัญที่สุดสำหรับคู่สัญญาต่างประเทศคือเงื่อนไขอำนาจกรรมการ ว่ากรรมการคนใดลงนามผูกพันบริษัทได้ ลงนามคนเดียวหรือกี่คนร่วมกัน และต้องประทับตราสำคัญหรือไม่",
      "หน่วยงานต่างประเทศมักกำหนดว่าหนังสือรับรองต้องออกใหม่ภายในกรอบเวลาหนึ่งก่อนวันยื่น กรอบเวลานี้ผันแปรตามผู้รับเอกสาร โดยทั่วไปที่พบคือ 3 หรือ 6 เดือน แต่ไม่มีกฎกลางของไทยกำหนดอายุของหนังสือรับรอง จึงต้องยืนยันกับผู้รับปลายทางก่อนขอเอกสาร ไม่ควรใช้ฉบับเก่าที่เก็บไว้",
      "กรมพัฒนาธุรกิจการค้าออกหนังสือรับรองเป็นภาษาไทยและมีบริการหนังสือรับรองภาษาอังกฤษด้วย หากใช้ฉบับภาษาไทยจะต้องแปลและนำคำแปลไปรับรองที่กองสัญชาติและนิติกรณ์ กรมการกงสุล การรับรองของกรมการกงสุลคือการรับรองลายมือชื่อและตราประทับของเจ้าหน้าที่ผู้ออกเอกสาร ไม่ใช่การรับรองสถานะทางการเงินหรือความน่าเชื่อถือทางธุรกิจ",
      "หากเอกสารจะใช้กับประเทศที่ไทยมีความตกลงยกเว้นหรือเมื่ออนุสัญญา Apostille มีผลกับไทยแล้ว ลำดับขั้นตอนจะสั้นลง แต่ก่อนวันที่อนุสัญญามีผลบังคับใช้ ให้ยึดลำดับเดิมคือกรมการกงสุลแล้วต่อด้วยสถานทูตปลายทาง",
    ],
    bodyEn: [
      "The certificate of incorporation is issued by the DBD registrar and states the company name, 13-digit registration number, incorporation date, registered capital, registered office, the list of directors and — most important for a foreign counterparty — the binding-signature condition: which director or combination of directors may sign for the company and whether the company seal is required.",
      "Foreign recipients usually require a recently issued certificate. That window varies by recipient; three or six months is common, but Thai law sets no expiry for the certificate itself, so confirm the requirement with the recipient before ordering rather than reusing an old copy.",
      "The DBD issues the certificate in Thai and also offers an English-language certificate service. If you use the Thai version, it must be translated and the translation certified by the Legalisation Division of the Department of Consular Affairs. That certification confirms the signature and seal of the issuing officer — it says nothing about the company's finances or creditworthiness.",
      "Where the destination is a country covered by an exemption arrangement, or once the Apostille Convention is in force for Thailand, the chain shortens. Before that date, follow the traditional order: Department of Consular Affairs, then the destination embassy.",
    ],
    documentsTh: [
      "หนังสือรับรองนิติบุคคลฉบับล่าสุดจากกรมพัฒนาธุรกิจการค้า (ขอที่สำนักงานหรือผ่านระบบ e-Service ของ DBD)",
      "คำแปลภาษาอังกฤษของหนังสือรับรอง หากใช้ฉบับภาษาไทย",
      "บัตรประชาชนหรือหนังสือเดินทางของกรรมการผู้ลงนาม",
      "หนังสือมอบอำนาจ หากให้ผู้อื่นดำเนินการยื่นแทน พร้อมสำเนาบัตรประชาชนผู้รับมอบอำนาจ",
      "ข้อมูลผู้รับเอกสารปลายทาง เช่น ชื่อธนาคาร หน่วยงานทะเบียน หรือคู่สัญญา เพื่อกำหนดว่าต้องผ่านสถานทูตใด",
    ],
    documentsEn: [
      "A recently issued DBD certificate of incorporation (from a DBD office or the DBD e-Service system)",
      "An English translation of the certificate if the Thai version is used",
      "Thai ID card or passport of the signing director",
      "A power of attorney if a representative files on your behalf, with a copy of the representative's ID",
      "Details of the destination recipient — bank, registry or counterparty — so the correct embassy step can be identified",
    ],
    chainTh: [
      "ขอหนังสือรับรองฉบับล่าสุดจากกรมพัฒนาธุรกิจการค้า และตรวจว่าเงื่อนไขอำนาจกรรมการตรงกับผู้ที่จะลงนามจริง",
      "จัดทำคำแปลภาษาอังกฤษให้ตรงตามตัวเอกสาร โดยเฉพาะชื่อบริษัท เลขทะเบียน และข้อความอำนาจกรรมการ",
      "ยื่นรับรองคำแปลและนิติกรณ์ที่กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ",
      "นำเอกสารที่ผ่านกรมการกงสุลไปรับรองต่อที่สถานทูตหรือสถานกงสุลของประเทศปลายทางในไทย หากปลายทางเรียก",
      "ส่งชุดเอกสารให้ผู้รับปลายทาง พร้อมเก็บสำเนาที่ผ่านการรับรองไว้อ้างอิงในไฟล์บริษัท",
    ],
    chainEn: [
      "Order a freshly issued certificate from the DBD and check that the binding-signature condition matches the person who will actually sign",
      "Prepare an English translation faithful to the original, especially the company name, registration number and signing-authority wording",
      "File for translation certification and legalisation at the Legalisation Division, Department of Consular Affairs",
      "Take the legalised set to the destination country's embassy or consulate in Thailand if that step is required",
      "Send the set to the recipient and keep certified copies in the company file for future reference",
    ],
    checklistTh: [
      "ชื่อบริษัทภาษาอังกฤษในคำแปลตรงกับที่จดทะเบียนไว้กับกรมพัฒนาธุรกิจการค้าทุกตัวอักษร",
      "เลขทะเบียนนิติบุคคล 13 หลักถูกต้องและอ่านชัดในสำเนาทุกฉบับ",
      "เงื่อนไขอำนาจกรรมการในหนังสือรับรองสอดคล้องกับผู้ลงนามในสัญญาหรือแบบฟอร์มปลายทาง",
      "วันที่ออกหนังสือรับรองอยู่ในกรอบเวลาที่ผู้รับปลายทางกำหนด",
      "ตรวจสอบกับสถานทูตปลายทางว่ารับเอกสารที่ผ่านกรมการกงสุลแล้วในรูปแบบใด และต้องนัดหมายล่วงหน้าหรือไม่",
    ],
    checklistEn: [
      "The English company name in the translation matches the name registered with the DBD exactly",
      "The 13-digit registration number is correct and legible on every copy",
      "The signing-authority condition matches whoever will sign the destination contract or form",
      "The issue date falls within the window the recipient requires",
      "Confirm with the destination embassy what format it accepts after consular legalisation and whether an appointment is needed",
    ],
    faqsTh: [
      {
        q: "หนังสือรับรองบริษัทมีอายุกี่เดือน",
        a: "กฎหมายไทยไม่ได้กำหนดวันหมดอายุของหนังสือรับรองนิติบุคคล แต่ผู้รับเอกสารปลายทาง เช่น ธนาคารหรือหน่วยงานทะเบียนต่างประเทศ มักกำหนดเองว่าต้องออกไม่เกิน 3 หรือ 6 เดือน จึงต้องยืนยันกับผู้รับก่อนขอเอกสาร",
      },
      {
        q: "ขอหนังสือรับรองเป็นภาษาอังกฤษจากกรมพัฒนาธุรกิจการค้าได้เลยหรือไม่",
        a: "กรมพัฒนาธุรกิจการค้ามีบริการหนังสือรับรองภาษาอังกฤษ เงื่อนไขและช่องทางการขอเป็นไปตามที่กรมกำหนดในแต่ละช่วง ควรตรวจสอบกับสำนักงานหรือระบบ e-Service ก่อน หากใช้ฉบับภาษาไทยก็ใช้วิธีแปลแล้วรับรองคำแปลที่กรมการกงสุลได้เช่นกัน",
      },
      {
        q: "ต้องผ่านสถานทูตปลายทางทุกครั้งหรือไม่",
        a: "ไม่เสมอไป ขึ้นกับข้อกำหนดของผู้รับเอกสาร บางประเทศและบางธุรกรรมรับเพียงการรับรองของกรมการกงสุล บางแห่งเรียกการรับรองจากสถานทูตด้วย จึงควรถามผู้รับปลายทางเป็นลายลักษณ์อักษรก่อนเริ่มขั้นตอน",
      },
      {
        q: "Apostille จะทำให้ขั้นตอนสั้นลงเมื่อใด",
        a: `อนุสัญญากรุงเฮก ค.ศ. 1961 จะมีผลบังคับใช้กับประเทศไทยในวันที่ ${APOSTILLE_STATUS.entryIntoForceTh} ตามข้อมูลของ HCCH ก่อนวันดังกล่าวยังต้องใช้ลำดับเดิมคือกรมการกงสุลแล้วต่อด้วยสถานทูตปลายทาง`,
      },
      {
        q: "กรรมการต่างชาติที่ไม่อยู่ในไทยจะลงนามอย่างไร",
        a: "ลงนามต่อหน้าโนตารีปับลิกในประเทศที่กรรมการอยู่ แล้วนำเอกสารไปรับรองตามลำดับของประเทศนั้นและรับรองที่สถานทูตไทยหรือหน่วยงานที่เกี่ยวข้องก่อนส่งกลับมาใช้ในไทย ลำดับที่แน่นอนผันแปรตามประเทศต้นทาง",
      },
      {
        q: "สำเนาหนังสือรับรองที่กรรมการรับรองสำเนาถูกต้องใช้แทนฉบับจริงได้หรือไม่",
        a: "ในงานนิติกรณ์เพื่อใช้ต่างประเทศ โดยทั่วไปต้องใช้ฉบับที่นายทะเบียนออกให้ ไม่ใช่สำเนาที่กรรมการรับรองเอง เว้นแต่ผู้รับปลายทางระบุไว้ชัดเจนว่ารับสำเนารับรองโดยทนายความหรือโนตารี",
      },
    ],
    faqsEn: [
      {
        q: "How long is a Thai company affidavit valid?",
        a: "Thai law sets no expiry date for the certificate itself. Recipients abroad — banks, registries, counterparties — usually impose their own limit, commonly three or six months from the issue date, so confirm the requirement before ordering.",
      },
      {
        q: "Can the DBD issue the certificate in English?",
        a: "The DBD offers an English-language certificate service; the channels and conditions are set by the department and can change, so check with a DBD office or the e-Service system. Alternatively, use the Thai version and have the translation certified by the Department of Consular Affairs.",
      },
      {
        q: "Is the embassy step always required?",
        a: "No. It depends on the recipient. Some countries and transactions accept consular legalisation alone; others require embassy certification as well. Ask the recipient in writing before starting the chain.",
      },
      {
        q: "When will the Apostille shorten this process?",
        a: `The 1961 Hague Convention enters into force for Thailand on ${APOSTILLE_STATUS.entryIntoForceEn} according to HCCH. Until then the traditional order applies: Department of Consular Affairs, then the destination embassy.`,
      },
      {
        q: "How does a foreign director abroad sign?",
        a: "The director signs before a notary public in their own country, then follows that country's legalisation chain and, where required, certification by the Thai embassy before the document is used in Thailand. The exact order varies by country of origin.",
      },
      {
        q: "Can a director-certified copy replace the original?",
        a: "For legalisation intended for overseas use, the registrar-issued document is normally required rather than a self-certified copy — unless the recipient states explicitly that a lawyer- or notary-certified copy is acceptable.",
      },
    ],
  },
  {
    slug: "board-resolution-and-shareholder-minutes",
    titleTh: "มติที่ประชุมกรรมการและรายงานการประชุมผู้ถือหุ้น",
    titleEn: "Board resolutions and shareholder meeting minutes",
    summaryTh:
      "เอกสารที่บริษัทจัดทำขึ้นเองเพื่อแสดงการตัดสินใจภายใน เช่น การเปิดบัญชีธนาคารต่างประเทศ การตั้งบริษัทลูก หรือการมอบอำนาจ ต้องมีองค์ประชุมและถ้อยคำที่ถูกต้องก่อนนำไปรับรองลายมือชื่อและใช้ต่างประเทศ",
    summaryEn:
      "Documents the company drafts itself to evidence an internal decision — opening an overseas bank account, incorporating a subsidiary, granting authority — which must show a proper quorum and precise wording before the signature is certified for overseas use.",
    bodyTh: [
      "มติที่ประชุมกรรมการและรายงานการประชุมผู้ถือหุ้นเป็นเอกสารภายในของบริษัท ไม่ใช่เอกสารราชการ หน่วยงานต่างประเทศจึงมักไม่รับเพียงกระดาษที่มีลายมือชื่อกรรมการ แต่จะขอให้มีการรับรองลายมือชื่อโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร หรือโนตารีปับลิกเสียก่อน แล้วจึงนำไปรับรองต่อที่กรมการกงสุลและสถานทูต",
      "การประชุมผู้ถือหุ้นของบริษัทจำกัดอยู่ภายใต้ประมวลกฎหมายแพ่งและพาณิชย์ บรรพ 3 ลักษณะ 22 ซึ่งกำหนดเรื่ององค์ประชุม การบอกกล่าวเรียกประชุม และมติพิเศษสำหรับเรื่องสำคัญ เช่น การแก้ไขหนังสือบริคณห์สนธิหรือข้อบังคับ ส่วนบริษัทมหาชนอยู่ภายใต้พระราชบัญญัติบริษัทมหาชนจำกัด พ.ศ. 2535 ซึ่งมีเงื่อนไขต่างออกไป การเลือกใช้ถ้อยคำ มติธรรมดา หรือ มติพิเศษ จึงต้องตรงกับประเภทของนิติบุคคลและเรื่องที่ลงมติ",
      "ถ้อยคำในมติควรระบุให้ชัดว่าที่ประชุมอนุมัติสิ่งใด ผู้ใดได้รับมอบหมายให้ลงนาม ขอบเขตอำนาจกว้างเพียงใด และมีกำหนดเวลาสิ้นสุดหรือไม่ ธนาคารและหน่วยงานทะเบียนต่างประเทศมักตีความตามตัวอักษร หากมติเขียนกว้างเกินไปหรือไม่ระบุชื่อผู้รับมอบอำนาจ เอกสารอาจถูกตีกลับ",
      "ควรแนบหนังสือรับรองบริษัทฉบับล่าสุดไปพร้อมกับมติเสมอ เพื่อให้ผู้รับปลายทางตรวจสอบได้ว่าผู้ลงนามในมติเป็นกรรมการผู้มีอำนาจตามทะเบียนจริงในวันที่ลงนาม",
    ],
    bodyEn: [
      "Board resolutions and shareholder minutes are internal company documents, not government records. Foreign recipients therefore rarely accept a page bearing only directors' signatures: they expect the signature to be certified first by a Notarial Services Attorney or notary public, after which the document can be legalised by the Department of Consular Affairs and the embassy.",
      "Shareholder meetings of a private limited company are governed by the Civil and Commercial Code, Book III, Title 22, which covers quorum, notice of meeting and the special resolutions required for major matters such as amending the memorandum or articles of association. Public companies fall under the Public Limited Companies Act B.E. 2535 (1992), with different conditions. The choice between an ordinary and a special resolution must therefore match both the entity type and the subject matter.",
      "The wording should state precisely what was approved, who is authorised to sign, how wide that authority runs and whether it expires. Foreign banks and registries read such documents literally; a resolution that is too broad or that omits the named attorney is frequently rejected.",
      "Always attach a recent company affidavit so the recipient can verify that the signatory was a duly authorised director on the date of signing.",
    ],
    documentsTh: [
      "ร่างมติที่ประชุมกรรมการหรือรายงานการประชุมผู้ถือหุ้น พร้อมวาระและวันที่ประชุมที่ชัดเจน",
      "หนังสือรับรองนิติบุคคลฉบับล่าสุด เพื่อยืนยันอำนาจกรรมการ ณ วันลงนาม",
      "บัญชีรายชื่อผู้ถือหุ้น (บอจ.5) หากเป็นมติของที่ประชุมผู้ถือหุ้น",
      "ข้อบังคับของบริษัท หากเรื่องที่ลงมติอยู่ภายใต้ข้อบังคับเฉพาะ",
      "บัตรประชาชนหรือหนังสือเดินทางของกรรมการหรือประธานที่ประชุมผู้ลงนาม",
    ],
    documentsEn: [
      "The draft board resolution or shareholder minutes with a clear agenda and meeting date",
      "A recent company affidavit confirming signing authority on the date of execution",
      "The shareholder list (Bor.Or.Jor.5) where the resolution is a shareholder resolution",
      "The articles of association where the matter is governed by a specific article",
      "ID card or passport of the signing director or meeting chairperson",
    ],
    chainTh: [
      "ร่างมติให้ตรงกับประเภทนิติบุคคลและเรื่องที่ลงมติ ตรวจว่าต้องเป็นมติธรรมดาหรือมติพิเศษ",
      "จัดประชุมและลงนามให้ครบตามองค์ประชุมและข้อบังคับของบริษัท",
      "ให้ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารรับรองลายมือชื่อผู้ลงนามในเอกสารฉบับภาษาอังกฤษ",
      "ยื่นรับรองที่กองสัญชาติและนิติกรณ์ กรมการกงสุล หากปลายทางเรียกเอกสารที่ผ่านนิติกรณ์",
      "รับรองต่อที่สถานทูตของประเทศปลายทางตามที่ผู้รับกำหนด แล้วจัดเก็บต้นฉบับไว้ในสมุดทะเบียนบริษัท",
    ],
    chainEn: [
      "Draft the resolution to match the entity type and subject matter, checking whether an ordinary or special resolution is required",
      "Hold the meeting and obtain signatures in line with the quorum and the articles of association",
      "Have a Notarial Services Attorney certify the signatures on the English-language version",
      "File for legalisation at the Legalisation Division, Department of Consular Affairs, where the recipient requires it",
      "Obtain destination-embassy certification if required, then keep the executed original in the company's statutory records",
    ],
    checklistTh: [
      "วันที่ประชุมและวันที่ลงนามสอดคล้องกัน และอยู่หลังวันที่ออกหนังสือรับรองที่แนบไปด้วย",
      "ชื่อและตำแหน่งผู้ได้รับมอบอำนาจสะกดตรงกับหนังสือเดินทางหรือบัตรประชาชน",
      "ขอบเขตอำนาจในมติครอบคลุมธุรกรรมที่จะทำจริง ไม่กว้างหรือแคบเกินไป",
      "จำนวนผู้เข้าประชุมครบองค์ประชุมตามข้อบังคับและกฎหมายที่ใช้บังคับ",
      "ฉบับภาษาอังกฤษและฉบับภาษาไทยมีเนื้อความตรงกัน หากจัดทำสองภาษา",
    ],
    checklistEn: [
      "The meeting date and signature date are consistent and fall after the issue date of the attached affidavit",
      "Names and titles of authorised persons are spelled exactly as in the passport or ID card",
      "The scope of authority covers the actual transaction — neither too broad nor too narrow",
      "Attendance satisfies the quorum required by the articles and the applicable statute",
      "The English and Thai versions carry identical substance where a bilingual document is used",
    ],
    faqsTh: [
      {
        q: "มติที่ประชุมต้องจดทะเบียนกับกรมพัฒนาธุรกิจการค้าหรือไม่",
        a: "ขึ้นกับเรื่องที่ลงมติ มติที่กระทบรายการทางทะเบียน เช่น การเปลี่ยนกรรมการ ที่ตั้งสำนักงาน หรือทุนจดทะเบียน ต้องยื่นจดทะเบียนเปลี่ยนแปลงกับกรมพัฒนาธุรกิจการค้า ส่วนมติที่เป็นเรื่องภายใน เช่น การอนุมัติเปิดบัญชีธนาคาร ไม่ต้องจดทะเบียน",
      },
      {
        q: "มติพิเศษต่างจากมติธรรมดาอย่างไร",
        a: "มติพิเศษใช้กับเรื่องสำคัญที่กฎหมายกำหนด เช่น การแก้ไขหนังสือบริคณห์สนธิหรือข้อบังคับ และต้องผ่านกระบวนการเรียกประชุมและคะแนนเสียงตามที่ประมวลกฎหมายแพ่งและพาณิชย์กำหนด ส่วนมติธรรมดาใช้กับเรื่องทั่วไปที่ไม่ได้ระบุไว้เป็นการเฉพาะ",
      },
      {
        q: "ทนายความรับรองลายมือชื่อคือการรับรองเนื้อหาในมติหรือไม่",
        a: "ไม่ใช่ การรับรองลายมือชื่อเป็นการยืนยันว่าผู้ลงนามได้ลงลายมือชื่อต่อหน้าผู้รับรองและเป็นบุคคลตามเอกสารแสดงตน ไม่ใช่การรับรองว่าข้อความในมติถูกต้องหรือมติชอบด้วยกฎหมาย",
      },
      {
        q: "ประชุมผ่านระบบออนไลน์ได้หรือไม่",
        a: "การประชุมผ่านสื่ออิเล็กทรอนิกส์ทำได้ภายใต้กฎหมายว่าด้วยการประชุมผ่านสื่ออิเล็กทรอนิกส์ โดยต้องมีระบบควบคุมการประชุมและการจัดเก็บข้อมูลตามมาตรฐานที่กำหนด ทั้งนี้รายละเอียดวิธีปฏิบัติผันแปรตามประเภทนิติบุคคล ควรตรวจสอบก่อนจัดประชุมที่ต้องใช้เอกสารกับต่างประเทศ",
      },
      {
        q: "ต้องแนบบัญชีรายชื่อผู้ถือหุ้นทุกครั้งหรือไม่",
        a: "ไม่จำเป็นทุกกรณี แต่หากเป็นมติของที่ประชุมผู้ถือหุ้นหรือปลายทางต้องการตรวจสอบโครงสร้างการถือหุ้น การแนบ บอจ.5 ฉบับล่าสุดช่วยลดการถูกสอบถามเพิ่ม",
      },
    ],
    faqsEn: [
      {
        q: "Must resolutions be filed with the DBD?",
        a: "Resolutions must be filed with the DBD depending on whether they alter registered corporate particulars. Resolutions modifying directors, registered offices, or share capital require formal filing with the Department of Business Development. Conversely, purely internal resolutions, such as approving bank account operations, do not require filing with the DBD.",
      },
      {
        q: "How does a special resolution differ from an ordinary one?",
        a: "Special resolutions apply to matters the statute reserves, such as amending the memorandum or articles, and require the notice and voting thresholds set out in the Civil and Commercial Code. Ordinary resolutions cover everything not specifically reserved.",
      },
      {
        q: "Does attorney certification confirm the content of the resolution?",
        a: "No. Signature certification confirms that the named person signed in the certifier's presence and matches their identity document. It does not confirm that the content is accurate or that the resolution is legally valid.",
      },
      {
        q: "Can the meeting be held online?",
        a: "Electronic meetings are permitted under Thailand's electronic meeting legislation, subject to control and record-keeping standards. The practical requirements vary by entity type, so verify them before holding a meeting whose minutes will be used abroad.",
      },
      {
        q: "Should the shareholder list always be attached?",
        a: "Not always, but attaching a current Bor.Or.Jor.5 helps when the resolution is a shareholder resolution or when the recipient wants to verify the ownership structure.",
      },
    ],
  },
  {
    slug: "corporate-power-of-attorney",
    titleTh: "หนังสือมอบอำนาจของนิติบุคคลเพื่อใช้ในต่างประเทศ",
    titleEn: "Corporate power of attorney for overseas use",
    summaryTh:
      "หนังสือมอบอำนาจที่บริษัทออกให้ผู้แทนไปดำเนินการแทนในต่างประเทศ เช่น จดทะเบียนบริษัทลูก เปิดบัญชี หรือลงนามสัญญา ต้องระบุขอบเขตอำนาจให้ชัดและผ่านการรับรองลายมือชื่อก่อนนำไปใช้",
    summaryEn:
      "A power of attorney by which the company appoints a representative to act abroad — incorporating a subsidiary, opening an account, signing a contract — which must define the scope precisely and carry certified signatures before it is used.",
    bodyTh: [
      "หนังสือมอบอำนาจของนิติบุคคลต้องลงนามโดยกรรมการผู้มีอำนาจตามที่ระบุในหนังสือรับรอง หากหนังสือรับรองระบุว่ากรรมการสองคนลงนามร่วมกันและประทับตรา การให้กรรมการคนเดียวลงนามจะทำให้เอกสารไม่ผูกพันบริษัทและมักถูกปฏิเสธที่ปลายทาง",
      "ขอบเขตอำนาจควรเขียนเป็นรายการที่ทำได้ชัดเจน เช่น ยื่นคำขอจดทะเบียนต่อหน่วยงานใด ลงนามในเอกสารประเภทใด และรับเงินหรือเอกสารคืนได้หรือไม่ การเขียนกว้างว่า ดำเนินการทุกอย่างที่เกี่ยวข้อง มักไม่พอสำหรับธนาคารและนายทะเบียนต่างประเทศที่ตีความตามตัวอักษร",
      "ในทางปฏิบัติ ปลายทางมักขอชุดเอกสารสามอย่างพร้อมกันคือ หนังสือมอบอำนาจ หนังสือรับรองบริษัทฉบับล่าสุด และสำเนาหนังสือเดินทางของทั้งผู้มอบและผู้รับมอบอำนาจ การเตรียมครบตั้งแต่แรกช่วยลดรอบการแก้ไข",
      "หากเอกสารต้องใช้ในประเทศที่ใช้ภาษาอื่น ควรจัดทำสองภาษาในฉบับเดียวกันหรือแนบคำแปลที่ผ่านการรับรอง เพื่อให้ผู้รับตรวจสอบข้อความได้โดยไม่ต้องแปลเอง",
    ],
    bodyEn: [
      "A corporate power of attorney must be signed by the directors authorised in the company affidavit. If the affidavit requires two directors signing jointly with the company seal, a single-director signature does not bind the company and is usually rejected at the destination.",
      "The scope should be written as an explicit list: which authority the representative may file with, which types of document they may sign, and whether they may receive funds or collect documents. Broad phrases such as 'to do all things necessary' rarely satisfy foreign banks and registrars, who read the text literally.",
      "In practice recipients typically ask for three items together: the power of attorney, a recent company affidavit and passport copies of both grantor and attorney. Preparing all three up front avoids repeated revision cycles.",
      "Where the document will be used in another language area, produce a bilingual instrument or attach a certified translation so the recipient can verify the text without commissioning its own translation.",
    ],
    documentsTh: [
      "ร่างหนังสือมอบอำนาจที่ระบุชื่อผู้มอบ ผู้รับมอบ ขอบเขตอำนาจ และระยะเวลา",
      "หนังสือรับรองนิติบุคคลฉบับล่าสุดที่แสดงเงื่อนไขอำนาจกรรมการ",
      "สำเนาหนังสือเดินทางหรือบัตรประชาชนของกรรมการผู้มอบอำนาจ",
      "สำเนาหนังสือเดินทางหรือบัตรประชาชนของผู้รับมอบอำนาจ",
      "ตราสำคัญของบริษัท หากหนังสือรับรองกำหนดให้ประทับตรา",
    ],
    documentsEn: [
      "A draft power of attorney naming grantor, attorney, scope of authority and duration",
      "A recent company affidavit showing the signing-authority condition",
      "Passport or ID copy of the granting director(s)",
      "Passport or ID copy of the appointed attorney",
      "The company seal where the affidavit requires it",
    ],
    chainTh: [
      "ตรวจเงื่อนไขอำนาจกรรมการในหนังสือรับรอง แล้วกำหนดผู้ลงนามให้ครบถ้วน",
      "ร่างหนังสือมอบอำนาจโดยระบุขอบเขตเป็นรายการและกำหนดวันสิ้นสุดอำนาจ",
      "ลงนามต่อหน้าทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร หรือโนตารีปับลิก",
      "นำไปรับรองนิติกรณ์ที่กรมการกงสุล และรับรองต่อที่สถานทูตปลายทางหากถูกเรียก",
      "ส่งฉบับจริงให้ผู้รับมอบอำนาจ พร้อมเก็บสำเนาและบันทึกวันสิ้นสุดอำนาจไว้ติดตาม",
    ],
    chainEn: [
      "Check the signing-authority condition in the affidavit and assemble the correct set of signatories",
      "Draft the power of attorney with an itemised scope and an expiry date",
      "Sign before a Notarial Services Attorney or notary public",
      "Legalise at the Department of Consular Affairs and, if required, at the destination embassy",
      "Send the original to the attorney and keep a copy, diarising the expiry date",
    ],
    checklistTh: [
      "ผู้ลงนามครบตามเงื่อนไขอำนาจกรรมการ และประทับตราหากกำหนดไว้",
      "ชื่อผู้รับมอบอำนาจสะกดตรงกับหนังสือเดินทางที่จะใช้แสดงตนที่ปลายทาง",
      "ขอบเขตอำนาจครอบคลุมทุกขั้นตอนที่ผู้รับมอบต้องทำจริง รวมถึงการรับเอกสารคืน",
      "ระบุวันสิ้นสุดอำนาจหรือเงื่อนไขการเพิกถอนไว้ชัดเจน",
      "แนบหนังสือรับรองบริษัทที่ออกใหม่ในกรอบเวลาที่ปลายทางยอมรับ",
    ],
    checklistEn: [
      "All required signatories have signed and the seal is affixed where mandated",
      "The attorney's name is spelled exactly as in the passport they will present abroad",
      "The scope covers every step the attorney must actually take, including collecting documents",
      "An expiry date or revocation condition is stated clearly",
      "A company affidavit issued within the recipient's acceptable window is attached",
    ],
    faqsTh: [
      {
        q: "หนังสือมอบอำนาจของบริษัทต้องติดอากรแสตมป์หรือไม่",
        a: "ประมวลรัษฎากรกำหนดอากรแสตมป์สำหรับตราสารบางประเภทรวมถึงใบมอบอำนาจ อัตราขึ้นกับลักษณะการมอบอำนาจว่ามอบให้บุคคลเดียวหรือหลายคนและมอบสำหรับกิจการครั้งเดียวหรือหลายครั้ง ควรตรวจสอบบัญชีอัตราอากรแสตมป์ท้ายประมวลรัษฎากรกับกรมสรรพากรก่อนลงนาม",
      },
      {
        q: "กำหนดอายุหนังสือมอบอำนาจไว้ดีหรือไม่",
        a: "ดี เพราะช่วยจำกัดความเสี่ยงหากผู้รับมอบอำนาจพ้นหน้าที่ และผู้รับเอกสารปลายทางหลายแห่งต้องการเห็นวันสิ้นสุดอำนาจที่ชัดเจน หากไม่ระบุ ควรมีกระบวนการเพิกถอนเป็นลายลักษณ์อักษร",
      },
      {
        q: "ผู้รับมอบอำนาจเป็นชาวต่างชาติได้หรือไม่",
        a: "ได้ กฎหมายไทยไม่ได้ห้าม แต่หน่วยงานปลายทางบางแห่งอาจมีเงื่อนไขเรื่องถิ่นที่อยู่หรือใบอนุญาตทำงานสำหรับการกระทำบางประเภท จึงต้องตรวจกับหน่วยงานปลายทางเป็นรายกรณี",
      },
      {
        q: "ใช้สำเนาหนังสือมอบอำนาจได้หรือไม่",
        a: "ส่วนใหญ่ปลายทางต้องการฉบับจริงที่ผ่านการรับรองตามลำดับ หากจำเป็นต้องใช้หลายที่พร้อมกัน ควรจัดทำหลายฉบับต้นฉบับและรับรองแยกกัน แทนการถ่ายสำเนาฉบับที่รับรองแล้ว",
      },
      {
        q: "ถ้ากรรมการเปลี่ยนหลังลงนามแล้ว หนังสือมอบอำนาจยังใช้ได้หรือไม่",
        a: "โดยหลักการ การมอบอำนาจที่ทำโดยชอบในขณะนั้นยังมีผล เว้นแต่บริษัทเพิกถอน แต่ในทางปฏิบัติผู้รับเอกสารต่างประเทศมักขอหนังสือรับรองฉบับใหม่ประกอบ จึงควรออกหนังสือมอบอำนาจใหม่เมื่อโครงสร้างกรรมการเปลี่ยน",
      },
    ],
    faqsEn: [
      {
        q: "Does a corporate power of attorney need stamp duty?",
        a: "The Revenue Code imposes stamp duty on certain instruments, including powers of attorney. The rate depends on whether authority is granted to one or several persons and for a single or multiple acts. Check the stamp duty schedule with the Revenue Department before signing.",
      },
      {
        q: "Should the power of attorney have an expiry date?",
        a: "Yes — it limits exposure if the attorney leaves the role, and many recipients want to see a clear end date. If no date is given, maintain a written revocation procedure.",
      },
      {
        q: "Can the attorney be a foreign national?",
        a: "Yes; Thai law does not prohibit it. Some destination authorities impose residence or work-permit conditions for particular acts, so verify case by case with the receiving authority.",
      },
      {
        q: "Can a photocopy be used?",
        a: "Most recipients require the legalised original. If several places need one simultaneously, execute and legalise multiple originals rather than copying a legalised one.",
      },
      {
        q: "Is the power of attorney still good if the directors change?",
        a: "In principle a validly granted authority survives until revoked, but foreign recipients usually ask for a current affidavit alongside it, so it is safer to reissue the power of attorney when the board composition changes.",
      },
    ],
  },
  {
    slug: "financial-statements-and-tax-documents",
    titleTh: "งบการเงิน ภ.พ.20 และหนังสือรับรองถิ่นที่อยู่ทางภาษี",
    titleEn: "Financial statements, VAT certificate and tax residence certificate",
    summaryTh:
      "ชุดเอกสารทางบัญชีและภาษีที่คู่ค้า ธนาคาร หรือหน่วยงานภาษีต่างประเทศเรียกดู ได้แก่ งบการเงินที่นำส่งกรมพัฒนาธุรกิจการค้า ทะเบียนภาษีมูลค่าเพิ่ม ภ.พ.20 และหนังสือรับรองถิ่นที่อยู่เพื่อการรัษฎากรจากกรมสรรพากร",
    summaryEn:
      "The accounting and tax set that foreign counterparties, banks or tax authorities request: financial statements filed with the DBD, the Por.Por.20 VAT registration certificate and the tax residence certificate issued by the Revenue Department.",
    bodyTh: [
      "งบการเงินของบริษัทจำกัดต้องได้รับการตรวจสอบโดยผู้สอบบัญชีรับอนุญาตและนำส่งกรมพัฒนาธุรกิจการค้าตามกำหนดเวลาที่กฎหมายกำหนดหลังปิดรอบบัญชี เอกสารที่ปลายทางยอมรับมักเป็นชุดที่มีหลักฐานการนำส่งของกรมพัฒนาธุรกิจการค้าประกอบ ไม่ใช่ไฟล์งบภายในที่ยังไม่ได้นำส่ง",
      "ภ.พ.20 คือใบทะเบียนภาษีมูลค่าเพิ่มที่กรมสรรพากรออกให้ผู้ประกอบการที่จดทะเบียน VAT ใช้แสดงต่อคู่ค้าเพื่อยืนยันว่าบริษัทอยู่ในระบบภาษีมูลค่าเพิ่มและออกใบกำกับภาษีได้ ผู้ประกอบการที่รายได้ยังไม่ถึงเกณฑ์บังคับจดทะเบียนอาจไม่มีเอกสารนี้ ซึ่งไม่ได้แปลว่าบริษัทไม่ถูกต้อง",
      "หนังสือรับรองถิ่นที่อยู่เพื่อการรัษฎากรใช้สำหรับขอใช้สิทธิประโยชน์ตามอนุสัญญาภาษีซ้อนที่ไทยทำกับประเทศคู่สัญญา เช่น การลดอัตราภาษีหัก ณ ที่จ่ายของเงินได้ที่จ่ายจากต่างประเทศ ผู้ขอต้องเป็นผู้มีถิ่นที่อยู่ในไทยเพื่อการรัษฎากรตามหลักเกณฑ์ของกรมสรรพากร และต้องยื่นตามแบบและช่องทางที่กรมกำหนด",
      "เอกสารทั้งสามกลุ่มนี้เมื่อจะใช้ต่างประเทศมักต้องแปลเป็นภาษาอังกฤษและผ่านการรับรองคำแปลที่กรมการกงสุล ตัวเลขในคำแปลต้องตรงกับต้นฉบับทุกหลัก เพราะผู้รับปลายทางมักตรวจยอดกับงบรวมของกลุ่มบริษัท",
    ],
    bodyEn: [
      "A limited company's financial statements must be audited by a licensed CPA and filed with the DBD within the statutory deadline after the accounting period closes. Recipients usually want the filed set together with proof of DBD submission, not an internal management account.",
      "Por.Por.20 is the VAT registration certificate issued by the Revenue Department to registered operators; it evidences that the company is inside the VAT system and may issue tax invoices. A company below the compulsory registration threshold may simply not hold one, which does not imply irregularity.",
      "A tax residence certificate supports claims under the double tax agreements Thailand has concluded — for example a reduced withholding rate on income paid from abroad. The applicant must be a Thai tax resident under Revenue Department criteria and must apply using the department's prescribed form and channel.",
      "For overseas use these documents generally need an English translation certified by the Department of Consular Affairs. Figures in the translation must match the original digit for digit, since recipients frequently reconcile them against group accounts.",
    ],
    documentsTh: [
      "งบการเงินที่ผ่านการตรวจสอบและนำส่งกรมพัฒนาธุรกิจการค้า พร้อมรายงานผู้สอบบัญชี",
      "หลักฐานการนำส่งงบการเงินต่อกรมพัฒนาธุรกิจการค้า",
      "ใบทะเบียนภาษีมูลค่าเพิ่ม ภ.พ.20 หากบริษัทจดทะเบียน VAT",
      "คำขอหนังสือรับรองถิ่นที่อยู่เพื่อการรัษฎากรตามแบบของกรมสรรพากร พร้อมเอกสารประกอบที่กรมกำหนด",
      "คำแปลภาษาอังกฤษของเอกสารที่จะใช้ต่างประเทศ",
    ],
    documentsEn: [
      "Audited financial statements filed with the DBD, including the auditor's report",
      "Proof of DBD submission of those statements",
      "The Por.Por.20 VAT certificate where the company is VAT-registered",
      "The Revenue Department application form for a tax residence certificate with its supporting documents",
      "English translations of any document intended for overseas use",
    ],
    chainTh: [
      "รวบรวมงบการเงินที่นำส่งแล้วและหลักฐานการนำส่งจากกรมพัฒนาธุรกิจการค้า",
      "ขอ ภ.พ.20 หรือหนังสือรับรองถิ่นที่อยู่เพื่อการรัษฎากรจากกรมสรรพากรตามแบบและช่องทางที่กรมกำหนด",
      "จัดทำคำแปลภาษาอังกฤษให้ตัวเลขและชื่อบัญชีตรงกับต้นฉบับ",
      "ยื่นรับรองคำแปลที่กองสัญชาติและนิติกรณ์ กรมการกงสุล",
      "รับรองต่อที่สถานทูตปลายทางหากผู้รับกำหนด แล้วส่งชุดเอกสารพร้อมสารบัญให้ผู้รับตรวจง่าย",
    ],
    chainEn: [
      "Collect the filed financial statements and the DBD submission evidence",
      "Apply to the Revenue Department for the Por.Por.20 or the tax residence certificate using the prescribed form and channel",
      "Prepare English translations in which figures and account captions match the original",
      "Have the translations certified by the Legalisation Division, Department of Consular Affairs",
      "Add destination-embassy certification where required and deliver the set with an index for easy review",
    ],
    checklistTh: [
      "รอบบัญชีในงบการเงินตรงกับปีที่ผู้รับปลายทางขอ",
      "ชื่อและเลขทะเบียนบริษัทในทุกเอกสารตรงกัน ทั้งฉบับไทยและคำแปล",
      "ตัวเลขในคำแปลตรงกับต้นฉบับทุกหลัก รวมถึงหน่วยเงินตราที่ระบุว่าเป็นบาท",
      "หนังสือรับรองถิ่นที่อยู่ระบุปีภาษีที่ต้องการใช้สิทธิตามอนุสัญญาภาษีซ้อน",
      "ตรวจว่าอนุสัญญาภาษีซ้อนระหว่างไทยกับประเทศปลายทางมีผลใช้บังคับอยู่จริงในปีภาษีนั้น",
    ],
    checklistEn: [
      "The accounting period matches the year the recipient asked for",
      "Company name and registration number are identical across every document and its translation",
      "Figures in the translation match the original digit for digit, with the currency stated as Thai baht",
      "The residence certificate names the tax year for which treaty benefits are claimed",
      "Verify that a double tax agreement between Thailand and the destination is actually in force for that tax year",
    ],
    faqsTh: [
      {
        q: "งบการเงินต้องนำส่งกรมพัฒนาธุรกิจการค้าเมื่อใด",
        a: "บริษัทจำกัดต้องจัดให้มีการตรวจสอบงบการเงินโดยผู้สอบบัญชีรับอนุญาต อนุมัติในที่ประชุมผู้ถือหุ้น และนำส่งกรมพัฒนาธุรกิจการค้าภายในกำหนดเวลาตามกฎหมายหลังวันสิ้นรอบบัญชี กำหนดเวลาที่แน่นอนและช่องทาง e-Filing ให้ตรวจสอบจากประกาศของกรมพัฒนาธุรกิจการค้าในปีนั้น",
      },
      {
        q: "ไม่มี ภ.พ.20 แปลว่าบริษัทผิดกฎหมายหรือไม่",
        a: "ไม่ใช่ ภ.พ.20 มีเฉพาะผู้ที่จดทะเบียนภาษีมูลค่าเพิ่ม ผู้ประกอบการที่รายได้ยังไม่ถึงเกณฑ์บังคับและไม่ได้จดทะเบียนโดยสมัครใจก็จะไม่มีเอกสารนี้",
      },
      {
        q: "หนังสือรับรองถิ่นที่อยู่ทางภาษีใช้ทำอะไร",
        a: "ใช้แสดงต่อหน่วยงานภาษีของประเทศคู่สัญญา เพื่อขอใช้สิทธิลดหรือยกเว้นภาษีตามอนุสัญญาภาษีซ้อนที่ไทยทำไว้กับประเทศนั้น เช่น กรณีเงินปันผล ดอกเบี้ย หรือค่าสิทธิที่จ่ายข้ามประเทศ",
      },
      {
        q: "ต้องให้ผู้สอบบัญชีลงนามในคำแปลด้วยหรือไม่",
        a: "โดยทั่วไปไม่ต้อง คำแปลจะผ่านการรับรองที่กรมการกงสุลตามกระบวนการนิติกรณ์ แต่ผู้รับบางรายอาจขอให้ผู้สอบบัญชียืนยันตัวเลขเพิ่มเติม ซึ่งเป็นข้อกำหนดของผู้รับ ไม่ใช่ของราชการไทย",
      },
      {
        q: "งบการเงินฉบับภาษาอังกฤษที่บริษัทจัดทำเองใช้ได้หรือไม่",
        a: "ใช้ได้ในการเจรจาธุรกิจ แต่หากปลายทางต้องการเอกสารที่ผ่านนิติกรณ์ จะต้องอ้างอิงจากงบที่นำส่งกรมพัฒนาธุรกิจการค้าและผ่านการรับรองคำแปลตามลำดับ",
      },
    ],
    faqsEn: [
      {
        q: "When must financial statements be filed with the DBD?",
        a: "A limited company must have its statements audited by a licensed CPA, approved by the shareholders' meeting and filed with the DBD within the statutory deadline after the year end. Check the DBD's announcements for the exact deadline and e-Filing channel in the relevant year.",
      },
      {
        q: "Does the absence of a Por.Por.20 mean the company is non-compliant?",
        a: "No. Only VAT-registered operators hold one. A company below the compulsory threshold that has not registered voluntarily simply will not have this certificate.",
      },
      {
        q: "What is the tax residence certificate used for?",
        a: "A tax residence certificate is used to claim reduced or exempt taxation under a double tax agreement with Thailand when presented to a treaty partner's tax authority. Foreign authorities typically require this document to grant tax relief or exemptions on cross-border income, such as dividends, interest, or royalty payments.",
      },
      {
        q: "Must the auditor sign the translation?",
        a: "Generally no; the translation is certified through the consular legalisation process. Some recipients additionally ask the auditor to confirm the figures, but that is the recipient's requirement, not a Thai government one.",
      },
      {
        q: "Can we use in-house English financial statements?",
        a: "In-house English financial statements can be used for informal commercial discussions, but cannot replace official documents when legalisation is required. Official legalised document sets must derive directly from the financial statements filed with the Department of Business Development (DBD) and undergo certified translation before processing.",
      },
    ],
  },
  {
    slug: "branch-representative-office-and-subsidiary",
    titleTh: "เอกสารตั้งสาขา สำนักงานผู้แทน และบริษัทลูกข้ามประเทศ",
    titleEn: "Documents for branches, representative offices and cross-border subsidiaries",
    summaryTh:
      "ชุดเอกสารบริษัทแม่ที่ต้องส่งไปจดทะเบียนกิจการในอีกประเทศหนึ่ง หรือชุดเอกสารบริษัทแม่ต่างชาติที่จะเข้ามาตั้งสาขาหรือสำนักงานผู้แทนในไทย ซึ่งต้องผ่านการรับรองครบสายก่อนยื่นต่อนายทะเบียน",
    summaryEn:
      "The parent-company set required to register an entity in another country, or the foreign parent's set required to establish a branch or representative office in Thailand, each of which must complete the full legalisation chain before filing with the registrar.",
    bodyTh: [
      "เมื่อบริษัทไทยไปจดทะเบียนบริษัทลูกหรือสาขาในต่างประเทศ นายทะเบียนปลายทางมักขอชุดเอกสารพื้นฐานสามอย่างคือ หนังสือรับรองบริษัทแม่ มติที่ประชุมอนุมัติการลงทุน และหนังสือมอบอำนาจให้ผู้ดำเนินการในประเทศนั้น ทั้งสามฉบับต้องผ่านการรับรองตามลำดับของไทยแล้วรับรองที่สถานทูตของประเทศปลายทาง",
      "ในทางกลับกัน บริษัทต่างชาติที่จะตั้งสาขาหรือสำนักงานผู้แทนในไทย ต้องนำเอกสารบริษัทแม่ที่ผ่านการรับรองจากประเทศต้นทางและรับรองที่สถานทูตไทยหรือช่องทางที่กำหนด มาแปลเป็นภาษาไทยและใช้ยื่นต่อกรมพัฒนาธุรกิจการค้า ประเภทธุรกิจบางรายการอยู่ภายใต้พระราชบัญญัติการประกอบธุรกิจของคนต่างด้าว พ.ศ. 2542 ซึ่งอาจต้องขอใบอนุญาตประกอบธุรกิจของคนต่างด้าวก่อน",
      "หากโครงการเข้าข่ายได้รับการส่งเสริมการลงทุน ผู้ลงทุนอาจยื่นคำขอรับการส่งเสริมต่อสำนักงานคณะกรรมการส่งเสริมการลงทุน ซึ่งมีเงื่อนไขและสิทธิประโยชน์เฉพาะตามประเภทกิจการ รายละเอียดประเภทกิจการและเงื่อนไขเปลี่ยนแปลงตามประกาศของ BOI จึงต้องตรวจสอบจากประกาศฉบับที่ใช้บังคับในขณะยื่น",
      "จุดที่ทำให้เอกสารตีกลับบ่อยที่สุดคือความไม่ตรงกันของชื่อบริษัทแม่ระหว่างเอกสารแต่ละฉบับ เช่น ใช้ตัวย่อในฉบับหนึ่งและเขียนเต็มในอีกฉบับ ควรกำหนดรูปแบบชื่อภาษาอังกฤษหนึ่งเดียวและใช้เหมือนกันทุกฉบับตั้งแต่ต้น",
    ],
    bodyEn: [
      "When a Thai company registers a subsidiary or branch abroad, the foreign registrar usually asks for three core items: the parent's certificate of incorporation, a resolution approving the investment and a power of attorney for the local incorporator. All three pass through Thailand's legalisation chain and then the destination embassy.",
      "In the opposite direction, a foreign company establishing a branch or representative office in Thailand must bring parent-company documents legalised in the home country and certified through the Thai embassy or another prescribed channel, translated into Thai for filing with the DBD. Certain activities fall under the Foreign Business Act B.E. 2542 (1999) and may require a foreign business licence first.",
      "If the project qualifies for investment promotion, the investor may apply to the Board of Investment, which grants activity-specific conditions and incentives. The eligible-activity list and conditions change with BOI announcements, so always check the version in force at the time of filing.",
      "The most frequent cause of rejection is an inconsistent parent-company name across documents — an abbreviation in one, the full form in another. Fix a single English name format at the outset and use it identically everywhere.",
    ],
    documentsTh: [
      "หนังสือรับรองบริษัทแม่ฉบับล่าสุด พร้อมคำแปลตามภาษาของประเทศปลายทาง",
      "มติที่ประชุมกรรมการหรือผู้ถือหุ้นอนุมัติการจัดตั้งกิจการในต่างประเทศ",
      "หนังสือมอบอำนาจให้ผู้ดำเนินการจดทะเบียนในประเทศปลายทาง",
      "หนังสือบริคณห์สนธิและข้อบังคับของบริษัทแม่ หากนายทะเบียนปลายทางเรียก",
      "หนังสือเดินทางของกรรมการและผู้แทนที่จะระบุชื่อในทะเบียนปลายทาง",
    ],
    documentsEn: [
      "A recent parent-company certificate with a translation in the destination language",
      "A board or shareholder resolution approving the overseas establishment",
      "A power of attorney for the local incorporator",
      "The parent's memorandum and articles of association where the registrar requires them",
      "Passports of the directors and representatives to be named on the foreign register",
    ],
    chainTh: [
      "ยืนยันกับที่ปรึกษาหรือนายทะเบียนปลายทางว่าต้องใช้เอกสารใดบ้างและต้องรับรองถึงขั้นใด",
      "จัดเตรียมเอกสารบริษัทแม่และมติที่เกี่ยวข้อง โดยใช้รูปแบบชื่อบริษัทเดียวกันทุกฉบับ",
      "รับรองลายมือชื่อโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารสำหรับเอกสารที่บริษัทจัดทำเอง",
      "ยื่นนิติกรณ์ที่กรมการกงสุล แล้วรับรองต่อที่สถานทูตของประเทศปลายทาง",
      "ส่งชุดเอกสารให้ผู้ดำเนินการปลายทางยื่นจดทะเบียน และติดตามผลจนได้เลขทะเบียนกิจการ",
    ],
    chainEn: [
      "Confirm with local counsel or the destination registrar exactly which documents are needed and to what level of certification",
      "Assemble the parent documents and resolutions using one consistent company-name format",
      "Have signatures on company-drafted documents certified by a Notarial Services Attorney",
      "Legalise at the Department of Consular Affairs, then at the destination embassy",
      "Send the set to the local incorporator for filing and follow through to the registration number",
    ],
    checklistTh: [
      "ชื่อบริษัทแม่ภาษาอังกฤษเขียนเหมือนกันทุกฉบับ ไม่มีตัวย่อสลับกับชื่อเต็ม",
      "มติอนุมัติระบุประเทศ ประเภทกิจการ และวงเงินลงทุนที่ตรงกับที่จะยื่นจริง",
      "ผู้รับมอบอำนาจในประเทศปลายทางมีเอกสารแสดงตนที่ยังไม่หมดอายุ",
      "ตรวจว่ากิจการที่จะทำในไทยเข้าข่ายพระราชบัญญัติการประกอบธุรกิจของคนต่างด้าวหรือไม่",
      "ตรวจประกาศ BOI ฉบับที่ใช้บังคับ หากจะยื่นขอรับการส่งเสริมการลงทุน",
    ],
    checklistEn: [
      "The parent's English name is written identically on every document, with no mix of abbreviation and full form",
      "The approving resolution names the country, activity and investment amount that will actually be filed",
      "The local attorney holds valid, unexpired identity documents",
      "Check whether the intended Thai activity falls under the Foreign Business Act",
      "Check the BOI announcement in force if investment promotion will be sought",
    ],
    faqsTh: [
      {
        q: "สาขาของบริษัทต่างชาติในไทยเป็นนิติบุคคลแยกจากบริษัทแม่หรือไม่",
        a: "ไม่ใช่ สาขาเป็นส่วนหนึ่งของบริษัทแม่ในต่างประเทศ ความรับผิดจึงตกอยู่ที่บริษัทแม่ ต่างจากบริษัทลูกที่จดทะเบียนในไทยซึ่งเป็นนิติบุคคลแยกต่างหากตามกฎหมายไทย",
      },
      {
        q: "สำนักงานผู้แทนทำธุรกิจหารายได้ในไทยได้หรือไม่",
        a: "สำนักงานผู้แทนมีขอบเขตกิจกรรมจำกัดตามที่กฎหมายและใบอนุญาตกำหนด โดยทั่วไปเป็นกิจกรรมสนับสนุนที่ไม่ก่อรายได้ในไทย รายละเอียดขอบเขตที่อนุญาตให้ตรวจสอบจากกรมพัฒนาธุรกิจการค้าและกฎหมายว่าด้วยการประกอบธุรกิจของคนต่างด้าว",
      },
      {
        q: "เอกสารบริษัทแม่ต่างชาติต้องแปลเป็นภาษาไทยหรือไม่",
        a: "ต้องแปล เพราะการยื่นต่อนายทะเบียนไทยใช้ภาษาไทย และคำแปลต้องมีความถูกต้องตรงกับต้นฉบับที่ผ่านการรับรองมาแล้วจากประเทศต้นทาง",
      },
      {
        q: "ต้องขอ BOI ก่อนจดทะเบียนบริษัทหรือไม่",
        a: "ลำดับขั้นตอนขึ้นกับประเภทกิจการและแนวปฏิบัติของ BOI ในขณะนั้น บางกรณียื่นคำขอรับการส่งเสริมก่อนแล้วจึงจัดตั้งนิติบุคคล บางกรณีจัดตั้งก่อน ควรตรวจสอบกับสำนักงานคณะกรรมการส่งเสริมการลงทุนเป็นรายโครงการ",
      },
      {
        q: "ใช้เอกสารชุดเดียวยื่นหลายประเทศพร้อมกันได้หรือไม่",
        a: "โดยทั่วไปไม่ได้ เพราะการรับรองขั้นสุดท้ายทำที่สถานทูตของแต่ละประเทศ จึงต้องเตรียมต้นฉบับแยกชุดตามจำนวนประเทศปลายทาง",
      },
    ],
    faqsEn: [
      {
        q: "Is a foreign company's Thai branch a separate legal entity?",
        a: "No. A branch is part of the foreign parent, so liability rests with the parent — unlike a Thai-registered subsidiary, which is a separate legal person under Thai law.",
      },
      {
        q: "May a representative office earn revenue in Thailand?",
        a: "A representative office has a limited scope fixed by law and its licence, generally non-revenue support activities. Confirm the permitted scope with the DBD and the foreign business legislation.",
      },
      {
        q: "Must the foreign parent's documents be translated into Thai?",
        a: "Yes. Filings with Thai registrars are made in Thai, and the translation must faithfully reflect the original already legalised in the home country.",
      },
      {
        q: "Should BOI approval come before incorporation?",
        a: "The sequence depends on the activity and BOI practice at the time; some projects apply for promotion first and incorporate afterwards, others the reverse. Confirm project by project with the Board of Investment.",
      },
      {
        q: "Can one legalised set serve several countries?",
        a: "One legalised document set cannot serve several countries because the final certification must be performed individually by each destination country's embassy. Applicants should prepare separate original document sets for each foreign country requiring legalisation to ensure compliance with specific consular procedures.",
      },
    ],
  },
  {
    slug: "certified-translation-of-corporate-documents",
    titleTh: "การแปลและรับรองคำแปลเอกสารนิติบุคคล",
    titleEn: "Certified translation of corporate documents",
    summaryTh:
      "หลักการแปลเอกสารบริษัทให้ผ่านการตรวจของกรมการกงสุลและผู้รับปลายทาง ตั้งแต่การสะกดชื่อบริษัท เลขทะเบียน ตำแหน่งกรรมการ ไปจนถึงศัพท์เฉพาะทางบัญชีและกฎหมายที่ต้องใช้ให้สม่ำเสมอทั้งชุด",
    summaryEn:
      "How corporate translations pass consular review and satisfy the recipient: consistent company names, registration numbers, director titles and the accounting and legal terminology used uniformly across the whole set.",
    bodyTh: [
      "การรับรองคำแปลที่กองสัญชาติและนิติกรณ์ กรมการกงสุล เป็นการตรวจว่าคำแปลตรงกับต้นฉบับ ไม่ใช่การรับรองว่าเนื้อหาต้นฉบับถูกต้องหรือบริษัทมีสถานะดี ดังนั้นคำแปลที่เพิ่มความหมาย ตัดข้อความ หรือปรับสำนวนให้สวยขึ้น มีโอกาสถูกตีกลับแม้ภาษาจะอ่านลื่นกว่า",
      "ชื่อบริษัทต้องใช้ชื่อภาษาอังกฤษที่จดทะเบียนไว้กับกรมพัฒนาธุรกิจการค้า ไม่ใช่การถอดเสียงใหม่ตามที่ผู้แปลเห็นควร หากบริษัทไม่มีชื่อภาษาอังกฤษจดทะเบียน ต้องใช้หลักการถอดอักษรที่สม่ำเสมอและใช้แบบเดียวกันในทุกเอกสารของชุดนั้น",
      "ตำแหน่งและคำเฉพาะควรใช้คำที่เป็นมาตรฐาน เช่น กรรมการ เป็น director, กรรมการผู้จัดการ เป็น managing director, ทุนจดทะเบียน เป็น registered capital และ หุ้นสามัญ เป็น ordinary shares การสลับไปมาระหว่างคำที่ความหมายใกล้เคียงกันในเอกสารชุดเดียวทำให้ผู้ตรวจสงสัยว่าเป็นคนละบริษัทหรือคนละรายการ",
      "สำหรับเอกสารที่มีตัวเลข เช่น งบการเงินและบัญชีรายชื่อผู้ถือหุ้น ให้คงรูปแบบตัวเลขและหน่วยตามต้นฉบับ ระบุสกุลเงินเป็นบาทอย่างชัดเจน และไม่ปัดเศษ เพราะผู้รับปลายทางมักตรวจยอดรวมกับเอกสารอื่นในชุดเดียวกัน",
    ],
    bodyEn: [
      "Certification by the Legalisation Division of the Department of Consular Affairs verifies that the translation matches the original — not that the original's content is correct or that the company is in good standing. Translations that add meaning, drop text or 'improve' the style are frequently returned even when they read better.",
      "The company name must be the English name registered with the DBD, not a fresh transliteration chosen by the translator. Where no English name is registered, apply a consistent transliteration and reuse it identically throughout the set.",
      "Use standard renderings for titles and defined terms: director, managing director, registered capital, ordinary shares. Alternating between near-synonyms within one set invites the reviewer to suspect that different companies or different items are involved.",
      "For numeric documents such as financial statements and shareholder lists, keep the original number format and units, state the currency as Thai baht explicitly and never round, because recipients reconcile totals across the set.",
    ],
    documentsTh: [
      "ต้นฉบับเอกสารนิติบุคคลที่ออกโดยหน่วยงานหรือที่บริษัทจัดทำและรับรองลายมือชื่อแล้ว",
      "รายการคำศัพท์เฉพาะของบริษัท เช่น ชื่อผลิตภัณฑ์ ชื่อสาขา และตำแหน่งภายใน เพื่อให้แปลตรงกันทั้งชุด",
      "ชื่อภาษาอังกฤษของบริษัทตามที่จดทะเบียนไว้ หากมี",
      "ข้อกำหนดของผู้รับปลายทางเรื่องรูปแบบคำแปล เช่น ต้องมีคำรับรองของผู้แปลหรือไม่",
      "สำเนาเอกสารเดิมที่เคยผ่านการรับรอง เพื่อใช้อ้างอิงคำแปลให้สม่ำเสมอ",
    ],
    documentsEn: [
      "The original corporate documents, whether issued by an authority or company-drafted with certified signatures",
      "A glossary of company-specific terms — product names, branch names, internal titles — so the whole set is consistent",
      "The company's registered English name, if one exists",
      "The recipient's formatting requirements, such as whether a translator's certificate is needed",
      "Previously certified translations for reference, to keep terminology stable",
    ],
    chainTh: [
      "รวบรวมต้นฉบับทั้งชุดก่อนเริ่มแปล เพื่อกำหนดคำศัพท์ให้สม่ำเสมอตั้งแต่แรก",
      "แปลโดยคงโครงสร้างและลำดับข้อความตามต้นฉบับ ไม่เพิ่มหรือตัดเนื้อหา",
      "ตรวจทานตัวเลข ชื่อบุคคล และเลขทะเบียนซ้ำอีกรอบเทียบกับต้นฉบับ",
      "ยื่นรับรองคำแปลที่กองสัญชาติและนิติกรณ์ กรมการกงสุล",
      "รับรองต่อที่สถานทูตปลายทางหากถูกเรียก และเก็บไฟล์คำแปลไว้ใช้อ้างอิงครั้งต่อไป",
    ],
    chainEn: [
      "Gather the whole set before translation begins so terminology can be fixed from the start",
      "Translate keeping the original structure and order, adding and removing nothing",
      "Proofread numbers, personal names and registration numbers again against the original",
      "File for certification at the Legalisation Division, Department of Consular Affairs",
      "Add embassy certification if required and archive the translation for future consistency",
    ],
    checklistTh: [
      "ชื่อบริษัทในคำแปลตรงกับชื่อภาษาอังกฤษที่จดทะเบียนไว้ทุกตัวอักษร",
      "เลขทะเบียนนิติบุคคล 13 หลักและเลขประจำตัวผู้เสียภาษีถูกต้อง",
      "ชื่อกรรมการสะกดตรงกับหนังสือเดินทาง ไม่ใช่การถอดเสียงใหม่",
      "ศัพท์เฉพาะใช้คำเดียวกันตลอดทั้งชุดเอกสาร",
      "ไม่มีข้อความที่ผู้แปลเพิ่มเข้ามาเอง เช่น หมายเหตุอธิบายที่ไม่มีในต้นฉบับ",
    ],
    checklistEn: [
      "The company name matches the registered English name exactly",
      "The 13-digit registration number and tax ID are correct",
      "Director names match their passports rather than a fresh transliteration",
      "Defined terms are rendered identically across the whole set",
      "No translator-added text, such as explanatory notes absent from the original",
    ],
    faqsTh: [
      {
        q: "ใครแปลเอกสารนิติบุคคลได้บ้าง",
        a: "กฎหมายไทยไม่ได้จำกัดว่าต้องเป็นนักแปลที่ขึ้นทะเบียนเท่านั้นสำหรับงานทั่วไป แต่คำแปลที่จะยื่นรับรองที่กรมการกงสุลต้องผ่านการตรวจของเจ้าหน้าที่ว่าตรงกับต้นฉบับ ผู้รับปลายทางบางรายอาจกำหนดเพิ่มว่าต้องเป็นผู้แปลที่ได้รับการรับรองในประเทศของตน",
      },
      {
        q: "ชื่อบริษัทที่ไม่เคยจดทะเบียนภาษาอังกฤษ ควรแปลอย่างไร",
        a: "ใช้หลักการถอดอักษรที่สม่ำเสมอและใช้รูปแบบเดียวกันในทุกเอกสารของชุด พร้อมคงชื่อภาษาไทยไว้ในวงเล็บหากผู้รับต้องการเทียบเคียง",
      },
      {
        q: "คำแปลต้องมีคำรับรองของผู้แปลหรือไม่",
        a: "ขึ้นกับผู้รับปลายทาง บางประเทศต้องการ certificate of translation accuracy ที่ผู้แปลลงนาม บางแห่งรับเพียงตรานิติกรณ์ของกรมการกงสุล ควรถามข้อกำหนดก่อนจัดทำ",
      },
      {
        q: "คำแปลเดิมที่เคยรับรองแล้วนำมาใช้ซ้ำได้หรือไม่",
        a: "ใช้อ้างอิงเพื่อความสม่ำเสมอของคำศัพท์ได้ แต่ถ้าต้นฉบับเป็นฉบับใหม่ เช่น หนังสือรับรองที่ออกใหม่ ต้องแปลและรับรองใหม่ตามต้นฉบับฉบับนั้น",
      },
      {
        q: "เอกสารสองภาษาในฉบับเดียวกันต้องรับรองคำแปลอีกหรือไม่",
        a: "หากเอกสารต้นฉบับจัดทำสองภาษาอยู่แล้วและผู้รับยอมรับ ก็อาจไม่ต้องแยกคำแปล แต่หลายหน่วยงานยังต้องการให้ผ่านนิติกรณ์อยู่ดี จึงควรยืนยันกับผู้รับก่อน",
      },
    ],
    faqsEn: [
      {
        q: "Who may translate corporate documents?",
        a: "Thai law does not restrict general translation to registered translators, but a translation submitted for consular certification is checked by officials against the original. Some foreign recipients additionally require a translator certified in their own country.",
      },
      {
        q: "How should a company name with no registered English form be handled?",
        a: "Apply a consistent transliteration used identically across the set, and keep the Thai name in brackets if the recipient needs to cross-check.",
      },
      {
        q: "Is a translator's certificate required?",
        a: "A translator's certificate requirement depends on the specific regulations of the recipient authority. While some foreign countries require a signed certificate of translation accuracy, others accept only the consular legalisation stamp. Applicants should verify the recipient's specific legalisation requirements before beginning the translation process.",
      },
      {
        q: "Can a previously certified translation be reused?",
        a: "Use it as a terminology reference, but a newly issued original — such as a fresh affidavit — must be translated and certified anew against that specific original.",
      },
      {
        q: "Do bilingual originals still need certified translation?",
        a: "If the original is already bilingual and the recipient accepts it, a separate translation may be unnecessary; many authorities still require legalisation, so confirm with the recipient first.",
      },
    ],
  },
];
